class OutlookServiceImpl {
  private item: Office.MessageCompose | Office.MessageRead | null = null

  async initialize(): Promise<void> {
    return new Promise((resolve) => {
      Office.onReady(() => {
        this.item = Office.context.mailbox.item as Office.MessageCompose | Office.MessageRead | null
        resolve()
      })
    })
  }

  async getCurrentItem(): Promise<Office.MessageCompose | Office.MessageRead | null> {
    return this.item
  }

  async getSelectedText(): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!this.item) {
        reject(new Error('No item selected'))
        return
      }

      this.item.body.getAsync(Office.CoercionType.Text, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve(result.value)
        } else {
          reject(result.error)
        }
      })
    })
  }

  async insertText(text: string, location: 'start' | 'end' | 'replace' = 'end'): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.item || !('body' in this.item)) {
        reject(new Error('Cannot insert text in read mode'))
        return
      }

      const composeItem = this.item as Office.MessageCompose
      
      if (location === 'replace') {
        composeItem.body.setAsync(text, { coercionType: Office.CoercionType.Html }, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve()
          } else {
            reject(result.error)
          }
        })
      } else {
        composeItem.body.prependAsync(text, { coercionType: Office.CoercionType.Html }, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve()
          } else {
            reject(result.error)
          }
        })
      }
    })
  }

  async insertHtml(html: string, location: 'start' | 'end' | 'replace' = 'end'): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.item || !('body' in this.item)) {
        reject(new Error('Cannot insert HTML in read mode'))
        return
      }

      const composeItem = this.item as Office.MessageCompose
      
      if (location === 'replace') {
        composeItem.body.setAsync(html, { coercionType: Office.CoercionType.Html }, (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve()
          } else {
            reject(result.error)
          }
        })
      } else {
        // For start/end, use prependAsync or appendAsync approach
        if (location === 'start') {
          composeItem.body.prependAsync(html, { coercionType: Office.CoercionType.Html }, (result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              resolve()
            } else {
              reject(result.error)
            }
          })
        } else {
          composeItem.body.setSelectedDataAsync(html, {
            coercionType: Office.CoercionType.Html
          }, (result) => {
            if (result.status === Office.AsyncResultStatus.Succeeded) {
              resolve()
            } else {
              reject(result.error)
            }
          })
        }
      }
    })
  }

  async getSubject(): Promise<string> {
    return new Promise((resolve, reject) => {
      if (!this.item) {
        reject(new Error('No item selected'))
        return
      }

      const subject = this.item.subject
      if (typeof subject === 'string') {
        // Read mode - subject is a string
        resolve(subject)
      } else {
        // Compose mode - subject is Subject object with getAsync
        subject.getAsync((result: Office.AsyncResult<string>) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve(result.value)
          } else {
            reject(result.error)
          }
        })
      }
    })
  }

  async setSubject(subject: string): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.item || !('subject' in this.item)) {
        reject(new Error('Cannot set subject in read mode'))
        return
      }

      const composeItem = this.item as Office.MessageCompose
      composeItem.subject.setAsync(subject, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve()
        } else {
          reject(result.error)
        }
      })
    })
  }

  async getRecipients(): Promise<Office.EmailAddressDetails[]> {
    return new Promise((resolve, reject) => {
      if (!this.item) {
        reject(new Error('No item selected'))
        return
      }

      if ('to' in this.item) {
        const composeItem = this.item as Office.MessageCompose
        composeItem.to.getAsync((result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve(result.value)
          } else {
            reject(result.error)
          }
        })
      } else {
        // In read mode, use the from field
        const readItem = this.item as Office.MessageRead
        resolve([readItem.from])
      }
    })
  }

  async addRecipients(recipients: string[], field: 'to' | 'cc' | 'bcc' = 'to'): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.item || !('to' in this.item)) {
        reject(new Error('Cannot add recipients in read mode'))
        return
      }

      const composeItem = this.item as Office.MessageCompose
      const recipientField = composeItem[field]
      
      recipientField.addAsync(recipients, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve()
        } else {
          reject(result.error)
        }
      })
    })
  }

  async addAttachment(name: string, content: string, isInline: boolean = false): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.item || !('addFileAttachmentAsync' in this.item)) {
        reject(new Error('Cannot add attachments in read mode'))
        return
      }

      const composeItem = this.item as Office.MessageCompose
      composeItem.addFileAttachmentAsync(content, name, { isInline, asyncContext: { name } }, (result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve()
        } else {
          reject(result.error)
        }
      })
    })
  }

  async getAttachments(): Promise<Office.AttachmentDetailsCompose[] | Office.AttachmentDetails[]> {
    if (!this.item) {
      throw new Error('No item selected')
    }

    // Check if item is in read mode
    if ('attachments' in this.item && Array.isArray((this.item as Office.MessageRead).attachments)) {
      return (this.item as Office.MessageRead).attachments
    }

    // Compose mode - need to use getAttachmentsAsync
    return new Promise((resolve, reject) => {
      const composeItem = this.item as Office.MessageCompose
      if (composeItem.getAttachmentsAsync) {
        composeItem.getAttachmentsAsync((result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve(result.value)
          } else {
            reject(result.error)
          }
        })
      } else {
        resolve([])
      }
    })
  }

  async saveAsDraft(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.item || !('saveAsync' in this.item)) {
        reject(new Error('Cannot save in read mode'))
        return
      }

      const composeItem = this.item as Office.MessageCompose
      composeItem.saveAsync((result) => {
        if (result.status === Office.AsyncResultStatus.Succeeded) {
          resolve()
        } else {
          reject(result.error)
        }
      })
    })
  }

  async getItemId(): Promise<string | null> {
    if (!this.item) {
      return null
    }

    // In read mode, itemId is available directly
    if ('itemId' in this.item && (this.item as Office.MessageRead).itemId) {
      return (this.item as Office.MessageRead).itemId
    }

    // In compose mode, we need to use getItemIdAsync if available
    return new Promise((resolve) => {
      const composeItem = this.item as Office.MessageCompose
      if (composeItem.getItemIdAsync) {
        composeItem.getItemIdAsync((result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            resolve(result.value)
          } else {
            resolve(null)
          }
        })
      } else {
        resolve(null)
      }
    })
  }

  async getConversationId(): Promise<string | null> {
    if (!this.item) {
      return null
    }

    return this.item.conversationId || null
  }

  async displayReplyForm(htmlBody: string): Promise<void> {
    if (!this.item || !('displayReplyForm' in this.item)) {
      throw new Error('Cannot display reply form in compose mode')
    }

    const readItem = this.item as Office.MessageRead
    readItem.displayReplyForm({ htmlBody })
  }

  async displayReplyAllForm(htmlBody: string): Promise<void> {
    if (!this.item || !('displayReplyAllForm' in this.item)) {
      throw new Error('Cannot display reply all form in compose mode')
    }

    const readItem = this.item as Office.MessageRead
    readItem.displayReplyAllForm({ htmlBody })
  }

  async close(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.item || !('close' in this.item)) {
        reject(new Error('Cannot close item'))
        return
      }

      const composeItem = this.item as Office.MessageCompose
      composeItem.close()
      resolve()
    })
  }
}

export const outlookService = new OutlookServiceImpl()